import {
  EngineBenefit,
  IEngineBenefitSelections,
} from "types/BenefitSelection/BenefitSelection";

/**
 * CLE-3100 — the values the benefit engine QUOTED from its ladder on a response whose cost share was
 * out-of-network only.
 *
 * CLE-2675 withholds every benefit field when `hasOnlyOutOfNetworkBenefits` is true, because the values
 * behind the flag were zeros of ignorance. Since CLE-3088 Part B that is no longer always so: an
 * out-of-network-only care setting counts as unanswered, and the engine runs its ladder — SUBSTITUTE (another
 * care setting the account uses as its substitute) then HISTORY (what this plan reported on earlier checks).
 * A value from either rung is a real answer, and the estimator shows it with the yellow CLE-2951 banner
 * (`EngineProvenanceBanner`). Withholding it saved an Eastgate estimate with a $0 copay where the engine had
 * $250 from 20 earlier patients on the same plan.
 *
 * So the form fills a benefit from such a response only when ALL of these hold:
 *   · the engine's value IS the quoted one (`quoted`) — on a legacy-quoted account it never is, and nothing
 *     changes there;
 *   · its rung is HISTORY or SUBSTITUTE — an ABSENT benefit (`OUT_OF_NETWORK_COST_SHARE_ONLY`) or any other
 *     rung stays withheld, exactly as CLE-2675 had it;
 *   · the response's own money field holds a number — that field is what pricing reads, and `quoted` says it
 *     equals the engine's value.
 *
 * A reported 0 from the ladder counts (Ani's acceptance case: 0% co-insurance from HISTORY).
 */

const LADDER_RUNGS: ReadonlySet<string> = new Set(["HISTORY", "SUBSTITUTE"]);

/** The response's own money field for each benefit the form fills. */
const RESPONSE_FIELD: Partial<Record<EngineBenefit, string>> = {
  FLAT_COPAY: "flatCopay",
  COINSURANCE_PERCENT: "coinsurancePercent",
  PROFESSIONAL_COPAY: "professionalCopay",
};

interface IResponseWithEngineSelections {
  engineBenefitSelections?: IEngineBenefitSelections | null;
  [field: string]: unknown;
}

export const engineLadderFilledValue = (
  response: IResponseWithEngineSelections | null | undefined,
  benefit: EngineBenefit
): number | null => {
  const field = RESPONSE_FIELD[benefit];
  const selection = response?.engineBenefitSelections?.benefits?.[benefit];
  if (!field || !selection || selection.quoted !== true) return null;
  if (!LADDER_RUNGS.has(selection.provenance?.rung)) return null;
  const value = response?.[field];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
};

/** True when at least one benefit field is filled from the ladder (see `engineLadderFilledValue`). */
export const hasEngineLadderFill = (
  response: IResponseWithEngineSelections | null | undefined
): boolean =>
  (Object.keys(RESPONSE_FIELD) as EngineBenefit[]).some(
    (benefit) => engineLadderFilledValue(response, benefit) !== null
  );
